import React, { useEffect, useRef } from 'react';
import { injectStripe } from 'react-stripe-elements';
import { CardNumberElement, CardExpiryElement, CardCVCElement } from 'react-stripe-elements';
import {
  Form,
  Row,
  Input,
  Label,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  UncontrolledTooltip,
} from 'reactstrap';
import {
  setCardNumber,
  setExpiryDate,
  setCardCvc,
  updateInputs,
  sendPayment,
} from '../actions/checkOutActions';
import { connect } from 'react-redux';

const createOptions = (fontSize?, padding?) => {
  return {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        letterSpacing: '0.025em',
        fontFamily: 'Source Code Pro, monospace',
        '::placeholder': {
          color: '#aab7c4',
        },
        padding,
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };
};

const StripeForm = ({
  checkout,
  shoppingCart,
  guest,
  auth,
  discount,
  fontSize,
  stripe,
  elements,
  setCardNumber,
  setExpiryDate,
  setCardCvc,
  updateInputs,
  sendPayment,
}: any) => {
  const prevPayButtonRef = useRef(checkout.payButton);

  useEffect(() => {
    if (checkout.payButton !== prevPayButtonRef.current) {
      if (checkout.payButton) {
        const cardElement = elements.getElement('cardNumber');

        stripe
          .createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: { name: checkout.nameOnCard },
          })
          .then(({ paymentMethod }) => {
            let data: any = {};
            data.firstName = checkout.firstname;
            data.lastName = checkout.lastname;
            data.address = checkout.address;
            data.suburb = checkout.suburb;
            data.postcode = checkout.postcode;
            data.phoneNumber = checkout.phoneNumber;
            data.message = checkout.description;
            data.shippingFee = checkout.shippingFee;
            data.shippingType = checkout.shippingMethod;
            data.date = checkout.date;
            data.email = checkout.email;
            data.pickupTime = checkout.pickupTime;
            data.paymentId = paymentMethod.id;
            data.cart = shoppingCart.cart;

            if (Object.keys(discount.userCoupon).length > 0) {
              data.coupon = discount.userCoupon;
            }
            if (auth.isAuthenticated) {
              data.userid = auth.user.id;
            } else if (guest.userid !== '') {
              data.userid = guest.userid;
            }

            sendPayment(data);
          });
      }
    }
    prevPayButtonRef.current = checkout.payButton;
  }, [checkout.payButton]);

  const handleChange = (e) => {
    if (e.elementType === 'cardNumber') {
      setCardNumber(e);
    }
    if (e.elementType === 'cardExpiry') {
      setExpiryDate(e);
    }
    if (e.elementType === 'cardCvc') {
      setCardCvc(e);
    }
  };

  const updateCardDetails = (e) => {
    var field = e.target.name;
    var inputValue = e.target.value;
    updateInputs(field, inputValue);
  };

  return (
    <Row>
      <Form className="w-100">
        <InputGroup>
          <Input
            name="nameOnCard"
            placeholder="Name on Card"
            className="my-2"
            onChange={updateCardDetails}
          />
        </InputGroup>

        <InputGroup>
          <CardNumberElement
            onChange={handleChange}
            className="my-2 form-control"
            {...createOptions(fontSize)}
          />
          <InputGroupAddon addonType="append" className="my-2">
            <InputGroupText id="cc-tooltip">
              <i className="fas fa-lock"></i>
              <UncontrolledTooltip placement="right" target="cc-tooltip">
                All transactions are secure and encrypted.
              </UncontrolledTooltip>
            </InputGroupText>
          </InputGroupAddon>
        </InputGroup>
        <CardExpiryElement
          onChange={handleChange}
          className="my-2 form-control"
          {...createOptions(fontSize)}
        />
        <InputGroup>
          <CardCVCElement
            onChange={handleChange}
            className="my-2 form-control"
            {...createOptions(fontSize)}
          />
          <InputGroupAddon addonType="append" className="my-2">
            <InputGroupText id="cvc-tooltip">
              <i className="fas fa-question-circle"></i>
              <UncontrolledTooltip placement="right" target="cvc-tooltip">
                3-digit security code usually found on the back of your card. American Express cards
                have a 4-digit code located on the front.
              </UncontrolledTooltip>
            </InputGroupText>
          </InputGroupAddon>
        </InputGroup>

        {checkout.errors.cardError && (
          <Label className="text-danger">{checkout.errors.cardError}</Label>
        )}
        {checkout.errors.stripeRefund && (
          <Label className="text-danger">{checkout.errors.stripeRefund}</Label>
        )}
      </Form>
    </Row>
  );
};

const mapStateToProps = (state: any) => ({
  checkout: state.checkout,
  shoppingCart: state.cart,
  guest: state.guest,
  auth: state.form,
  discount: state.discount,
});

export default connect(mapStateToProps, {
  setCardNumber,
  setExpiryDate,
  setCardCvc,
  updateInputs,
  sendPayment,
})(injectStripe(StripeForm));
